import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { 
  Activity, 
  Shield, 
  Users, 
  Zap, 
  BarChart, 
  Globe,
  CheckCircle2,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Layers,
  Database,
  Lock,
  Cpu,
  Network,
  LineChart
} from 'lucide-react';

// Hero Section
const HeroSection = () => {
  return (
    <section data-testid="hero-section" className="bg-slate-900 py-6 px-6">
      {/* Navigation Header - Outside the image container */}
      <nav className="w-full mb-6">
        <div className="container mx-auto max-w-6xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-500 flex items-center justify-center">
              <Activity className="h-5 w-5 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="font-manrope font-bold text-white text-lg leading-tight">SignalPath</span>
              <span className="text-slate-400 text-xs">A Digital Health Applications Company</span>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#connecting-your-life" className="text-slate-300 hover:text-white transition-colors text-sm">Connecting Your Life</a>
            <a href="#featured-product" className="text-slate-300 hover:text-white transition-colors text-sm">Featured Product</a>
            <a href="#our-philosophy" className="text-slate-300 hover:text-white transition-colors text-sm">Our Philosophy</a>
            <a href="/contact" className="text-slate-300 hover:text-white transition-colors text-sm">Contact</a>
          </div>
        </div>
      </nav>

      {/* Hero Image Container with margins */}
      <div 
        className="relative container mx-auto max-w-6xl rounded-2xl overflow-hidden text-white bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(to right, rgba(15, 23, 42, 0.85) 0%, rgba(15, 23, 42, 0.5) 50%, rgba(15, 23, 42, 0.2) 100%), url('https://images.unsplash.com/photo-1732129547874-fa3c641e94fc?fm=jpg&q=80&w=2000&auto=format&fit=crop')`,
          backgroundPosition: 'center center',
          backgroundSize: 'cover',
          minHeight: '70vh'
        }}
      >
        {/* Hero Content */}
        <div className="px-8 py-16 md:px-12 md:py-20 flex items-center h-full" style={{ minHeight: '70vh' }}>
          <div className="max-w-2xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 mb-6">
              <div className="w-2 h-2 rounded-full bg-teal-400"></div>
              <span className="text-sm text-slate-300">Stronger Every Day</span>
            </div>
            
            <h1 
              data-testid="hero-title"
              className="font-manrope text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 leading-tight"
            >
              Building a<br />
              Stronger Future<br />
              through <span className="text-teal-400">Active<br />
              Diabetes Care</span>
            </h1>
            
            <p className="text-xl text-white mb-8 font-semibold pl-4 border-l-4 border-teal-400">
              Empowering individuals in their diabetes management journey.
            </p>
            
            <p 
              data-testid="hero-subtitle"
              className="text-lg text-slate-200 max-w-xl mb-8 leading-relaxed font-medium"
            >
              We develop digital health applications that transform daily health data into meaningful insights, empowering individuals to navigate their diabetes journey with clarity and confidence.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Icon Grid Section
const IconGridSection = () => {
  const features = [
    {
      number: '01',
      title: 'Meal and Calorie Logging',
      description: 'Your digital life tells a story—and every meal is a chapter. Track what you eat, monitor calories, and discover patterns in your nutrition that help you make smarter choices for better glucose control.',
      image: 'https://images.pexels.com/photos/12499374/pexels-photo-12499374.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      number: '02',
      title: 'Activity Tracking',
      description: 'Your digital life tells a story of movement and progress. Log your workouts, steps, and daily activities to see how physical exercise influences your energy levels and overall diabetes management.',
      image: 'https://images.unsplash.com/photo-1552674605-db6ffd4facb5?w=800&q=80'
    },
    {
      number: '03',
      title: 'Blood Sugar Tracking',
      description: 'Your digital life tells a story written in numbers. Record your glucose readings throughout the day, identify trends, and understand how your lifestyle choices directly impact your blood sugar levels.',
      image: 'https://images.pexels.com/photos/17043386/pexels-photo-17043386.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      number: '04',
      title: 'Mood and Wellness Logging',
      description: 'Your digital life tells a story of how you feel. Track your emotional well-being, stress levels, and sleep quality to uncover connections between your mental state and physical health.',
      image: 'https://images.pexels.com/photos/3822625/pexels-photo-3822625.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      number: '05',
      title: 'Daily/Weekly Journaling',
      description: 'Capture your thoughts, reflections, and health observations in a structured journal to track patterns and celebrate progress over time.',
      image: 'https://images.unsplash.com/photo-1517842645767-c639042777db?w=800&q=80'
    }
  ];

  return (
    <section id="connecting-your-life" data-testid="icon-grid-section" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <p className="text-teal-500 font-semibold tracking-widest uppercase mb-3 text-sm">
            Connecting Your Life
          </p>
          <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            Here's Where Your Digital<br />
            <span className="text-teal-500">Transformation Begins</span>
          </h2>
        </div>
        <p className="text-center text-slate-600 max-w-3xl mx-auto mb-16">
          At SignalPath, we develop digital health applications designed to bring clarity to the daily complexity of diabetes management. By transforming everyday inputs—meals, activity, glucose patterns, and personal observations—into structured insights, our tools help individuals recognize patterns, build awareness, and make more informed decisions over time. We believe better understanding leads to greater confidence, and greater confidence leads to sustainable progress.
        </p>

        {/* Comprehensive Platform Content */}
        <Separator className="mb-16" />
        <div className="text-center mb-16">
          <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            SignalPath is built<br />
            <span className="text-teal-500">around one simple idea:</span><br />
            your digital life tells a story
          </h2>
        </div>
        
        <div className="space-y-24 max-w-6xl mx-auto">
          {features.map((feature, index) => (
            <div 
              key={index}
              data-testid={`numbered-feature-${index}`}
              className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 items-center`}
            >
              <div className="w-full md:w-1/2 mb-6 md:mb-0">
                <img 
                  src={feature.image} 
                  alt={feature.title}
                  className="w-full h-[300px] object-cover rounded-2xl shadow-lg"
                />
              </div>
              <div className="w-full md:w-1/2 p-4">
                <span className="text-teal-500 font-bold text-5xl opacity-30">{feature.number}</span>
                <h3 className="font-manrope text-2xl font-bold text-slate-900 mt-2 mb-4">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Product Showcase Section with Carousel - A1C Survival Plan
const ProductShowcaseSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const showcaseImages = [
    {
      url: 'https://customer-assets.emergentagent.com/job_3da1c7df-0ce6-4bcf-afe7-e3f44a8aed6b/artifacts/0stt2vil_A1C%20SUVPL%20APP%20LANDING%20PG%202025-12-27%20123651.png',
      title: 'A1C Survival Plan Home Dashboard'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_3da1c7df-0ce6-4bcf-afe7-e3f44a8aed6b/artifacts/fh6raw0w_A1C%20SUVPL%20APP-Journal-%202026-01-06%20161326.png',
      title: 'A1C Survival Plan Journal'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_3da1c7df-0ce6-4bcf-afe7-e3f44a8aed6b/artifacts/1aqmbxbo_A1C%20suvpl%20app-Diabetes%20Mgt%202026-01-06%20155009.png',
      title: 'A1C Survival Plan Diabetes Management'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_3da1c7df-0ce6-4bcf-afe7-e3f44a8aed6b/artifacts/mhcn03p1_A1C%20SUVPL%20APP-fitness%202026-01-06%20155606.png',
      title: 'A1C Survival Plan Fitness'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_3da1c7df-0ce6-4bcf-afe7-e3f44a8aed6b/artifacts/8pno2udx_A1C%20SUVPL%20APP-NUTRITION%202026-01-05%20131130.png',
      title: 'A1C Survival Plan Nutrition'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_3da1c7df-0ce6-4bcf-afe7-e3f44a8aed6b/artifacts/w47h81pg_A1C%20SUVPL%20APP-%20Mental%20Wellness-2026-01-02%20134830.png',
      title: 'A1C Survival Plan Mental Wellness'
    },
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % showcaseImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + showcaseImages.length) % showcaseImages.length);
  };

  useEffect(() => {
    const timer = setInterval(nextSlide, 4000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section id="featured-product" data-testid="product-showcase-section" className="py-24 bg-slate-50">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-teal-500 font-semibold tracking-widest uppercase mb-3 text-sm">
            Featured Product
          </p>
          <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-6">
            A1C Survival Plan is built<br />
            <span className="text-teal-500">around one simple idea:</span><br />
            your daily life tells a story
          </h2>
          <p className="text-slate-600 max-w-3xl mx-auto">
            At the center is your Journal — a place to document meals, glucose, activity, hydration, mood, and daily context. Each entry feeds into a broader picture, revealing patterns over time and turning everyday inputs into meaningful signals.
          </p>
        </div>
        
        <div className="relative max-w-4xl mx-auto">
          {/* Carousel Container */}
          <div className="bg-gradient-to-br from-slate-200 to-slate-300 p-8 md:p-12 rounded-3xl shadow-2xl">
            <div className="relative overflow-hidden">
              <div 
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {showcaseImages.map((image, index) => (
                  <div key={index} className="w-full flex-shrink-0 flex justify-center items-center">
                    {/* iPhone Mockup Frame */}
                    <div className="relative">
                      {/* Phone Outer Frame */}
                      <div className="bg-slate-900 rounded-[3rem] p-2 shadow-2xl border-4 border-slate-700">
                        {/* Phone Inner Bezel */}
                        <div className="bg-black rounded-[2.5rem] p-3 relative">
                          {/* Dynamic Island / Notch */}
                          <div className="absolute top-4 left-1/2 -translate-x-1/2 w-28 h-7 bg-black rounded-full z-10 flex items-center justify-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-slate-800"></div>
                            <div className="w-12 h-4 rounded-full bg-slate-800"></div>
                          </div>
                          {/* Screen Content */}
                          <div className="bg-white rounded-[2rem] overflow-hidden">
                            <img 
                              src={image.url} 
                              alt={image.title}
                              className="h-[500px] w-auto object-contain"
                            />
                          </div>
                          {/* Home Indicator */}
                          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 bg-slate-600 rounded-full"></div>
                        </div>
                      </div>
                      {/* Phone Reflection/Glow */}
                      <div className="absolute -inset-4 bg-teal-500/20 rounded-[4rem] blur-2xl -z-10"></div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Navigation Buttons */}
              <button 
                onClick={prevSlide}
                data-testid="carousel-prev"
                className="absolute left-0 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-teal-500 shadow-lg flex items-center justify-center hover:bg-teal-600 transition-colors z-20"
              >
                <ChevronLeft className="h-6 w-6 text-white" />
              </button>
              <button 
                onClick={nextSlide}
                data-testid="carousel-next"
                className="absolute right-0 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-teal-500 shadow-lg flex items-center justify-center hover:bg-teal-600 transition-colors z-20"
              >
                <ChevronRight className="h-6 w-6 text-white" />
              </button>
            </div>
            
            {/* Image Title */}
            <p className="text-center text-slate-700 font-semibold mt-6 text-lg">
              {showcaseImages[currentSlide].title}
            </p>
            
            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-4">
              {showcaseImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  data-testid={`carousel-dot-${index}`}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    currentSlide === index 
                      ? 'bg-teal-500 w-8' 
                      : 'bg-slate-400 hover:bg-slate-500'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        
        {/* Call to Action */}
        <div className="text-center mt-12">
          <p className="text-slate-600 mb-4">A1C Survival Plan organizes and connects life's little moments into one clear picture.</p>
          <a 
            href="https://a1csurvivalplan.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            Learn More About A1C Survival Plan
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

// Feature Grid Detailed Section
const FeatureGridDetailedSection = () => {
  return (
    <section 
      data-testid="feature-grid-section" 
      className="py-24 bg-cover bg-center bg-no-repeat relative"
      style={{
        backgroundImage: `linear-gradient(to right, rgba(15, 23, 42, 0.9) 0%, rgba(15, 23, 42, 0.7) 50%, rgba(15, 23, 42, 0.4) 100%), url('https://images.unsplash.com/photo-1467003909585-2f8a72700288?fm=jpg&q=80&w=2000&auto=format&fit=crop')`,
        backgroundPosition: 'center 70%',
      }}
    >
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-teal-400 font-semibold tracking-widest uppercase mb-3 text-sm">
            Capturing life's little moments
          </p>
          <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-white tracking-tight">
            Organized through<br />
            <span className="text-teal-400">intelligent digital tools</span>
          </h2>
          <p className="text-slate-300 max-w-3xl mx-auto mt-4">
            Transforming everyday signals into meaningful insight
          </p>
        </div>
        
        <div className="max-w-2xl">
          <h3 className="font-manrope text-3xl sm:text-4xl font-bold tracking-tight mb-6 leading-tight text-white">
            Nourish Your Body,<br />
            <span className="text-teal-400">Transform Your Health</span>
          </h3>
          <p className="text-lg text-slate-200 leading-relaxed">
            Track your meals, understand your nutrition, and make informed decisions that support your diabetes management journey.
          </p>
        </div>
      </div>
    </section>
  );
};

// Philosophy Section
const PhilosophySection = () => {
  return (
    <section id="our-philosophy" data-testid="philosophy-section" className="py-24 bg-white">
      <div className="container mx-auto px-6">
        <div className="text-center max-w-4xl mx-auto">
          <p className="text-teal-500 font-semibold tracking-widest uppercase mb-3 text-sm">
            Our Philosophy
          </p>
          <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-12">
            Developed with Long-Term<br />
            <span className="text-teal-500">Success in Focus</span>
          </h2>
          
          <p className="text-slate-600 mb-8 text-lg leading-relaxed">
            At SignalPath, we believe that clarity creates momentum. By transforming everyday health signals into structured insight, we help individuals move from uncertainty to understanding and from understanding to confident action.
          </p>
          <p className="text-slate-600 text-lg leading-relaxed">
            Our vision is to build digital applications that empower people not just to manage their condition, but to better understand their own path forward.
          </p>
        </div>
      </div>
    </section>
  );
};

// Testimonial Section
// Final CTA Section
const FinalCTASection = () => {
  return (
    <section data-testid="final-cta-section" className="py-32 bg-teal-500">
      <div className="container mx-auto px-6 text-center">
        <h2 className="font-manrope text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6">
          Build a Stronger Future Through<br />
          <span className="text-teal-100">Active Diabetes Care</span>
        </h2>
        <p className="text-teal-100 text-lg max-w-2xl mx-auto">
          Capture the moments that matter. Track, reflect, and organize your diabetes care all in one place.
        </p>
      </div>
    </section>
  );
};

// Footer Section
const FooterSection = () => {
  return (
    <footer data-testid="footer-section" className="bg-slate-900">
      {/* Main Footer Content */}
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
                <Activity className="h-5 w-5 text-white" />
              </div>
              <span className="font-manrope font-bold text-white text-lg">SignalPath Digital Health Applications, LLC</span>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-4">
              Building a Stronger Future through Active Diabetes Care. Your comprehensive companion for diabetes management and overall wellness.
            </p>
            <a href="mailto:support@signalpathdigitalapps.com" className="text-emerald-400 hover:text-emerald-300 text-sm transition-colors">
              support@signalpathdigitalapps.com
            </a>
          </div>

          {/* Resources Column */}
          <div>
            <h3 className="font-manrope font-bold text-white text-lg mb-6">Resources</h3>
            <ul className="space-y-4">
              <li>
                <a href="/contact" className="text-slate-400 hover:text-emerald-400 transition-colors text-sm">
                  Contact Us
                </a>
              </li>
              <li>
                <a href="/privacy-policy" className="text-slate-400 hover:text-emerald-400 transition-colors text-sm">
                  Privacy Policy
                </a>
              </li>
              <li>
                <a href="/terms-of-service" className="text-slate-400 hover:text-emerald-400 transition-colors text-sm">
                  Terms of Service
                </a>
              </li>
            </ul>
          </div>

          {/* Product Column */}
          <div>
            <h3 className="font-manrope font-bold text-white text-lg mb-6">Product</h3>
            <ul className="space-y-4">
              <li>
                <a href="#featured-product" className="text-slate-400 hover:text-emerald-400 transition-colors text-sm">
                  Featured Product
                </a>
              </li>
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h3 className="font-manrope font-bold text-white text-lg mb-6">Company</h3>
            <ul className="space-y-4">
              <li>
                <a href="/about-us" className="text-slate-400 hover:text-emerald-400 transition-colors text-sm">
                  About Us
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="container mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} SignalPath Digital Health Applications, LLC. All rights reserved.
            </p>
            
            {/* Social Icons - Removed */}
            <div className="flex items-center gap-6">
              {/* Made with Emergent */}
              <a 
                href="https://app.emergent.sh/?utm_source=emergent-badge"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-slate-500 hover:text-white transition-colors"
              >
                <img 
                  src="https://avatars.githubusercontent.com/in/1201222?s=120&u=2686cf91179bbafbc7a71bfbc43004cf9ae1acea&v=4"
                  alt="Emergent"
                  className="w-5 h-5 rounded"
                />
                <span className="text-sm">Made with Emergent</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page Component
const LandingPage = () => {
  return (
    <div data-testid="landing-page" className="min-h-screen">
      <HeroSection />
      <IconGridSection />
      <ProductShowcaseSection />
      <FeatureGridDetailedSection />
      <PhilosophySection />
      <FinalCTASection />
      <FooterSection />
    </div>
  );
};

export default LandingPage;
